import React from 'react';
import TrajectoryChart from './TrajectoryChart';
import AccuracyBadge from './AccuracyBadge';
import { formatValue, formatRange } from './format';

// Is the projection heading toward or away from the target? Direction-aware,
// so "falling resting HR" reads as improving while "falling steps" doesn't.
export const assess = (m) => {
  const t = m.target || {};
  if (m.trend === 'flat' || m.projected_value == null || m.fit_now == null) {
    return { text: 'holding steady', tone: 'flat' };
  }
  const good = { text: 'improving', tone: 'good' };
  const bad = { text: 'drifting off target', tone: 'bad' };
  if (m.direction === 'up') return m.trend === 'rising' ? good : bad;
  if (m.direction === 'down') return m.trend === 'falling' ? good : bad;
  // range: closer to the band is better
  const dist = (v) => {
    if (t.ultimate_low != null && v < t.ultimate_low) return t.ultimate_low - v;
    if (t.ultimate_high != null && v > t.ultimate_high) return v - t.ultimate_high;
    return 0;
  };
  return dist(m.projected_value) <= dist(m.fit_now) ? good : bad;
};

export const isInBand = (m) => {
  const t = m.target || {};
  const v = t.baseline;
  if (v == null) return false;
  if (m.direction === 'up') return t.ultimate_low != null && v >= t.ultimate_low;
  if (m.direction === 'down') return t.ultimate_high != null && v <= t.ultimate_high;
  return (
    t.ultimate_low != null && t.ultimate_high != null &&
    v >= t.ultimate_low && v <= t.ultimate_high
  );
};

const toneChip = {
  good: 'bg-good/15 text-good-strong dark:text-good',
  bad: 'bg-off/15 text-off-strong dark:text-off',
  flat: 'bg-gray-100 text-gray-700 dark:bg-surface-dark-raised dark:text-gray-300',
};

// This week's public commitment, plus how last week's turned out. The number
// is the twin's, decided when the week began; the verdict grades the twin,
// never the user. "No track record yet" is shown honestly for the first weeks.
const PredictionBlock = ({ prediction, unit }) => {
  if (!prediction) return null;
  const { current, last_graded: last } = prediction;

  const verdict = (() => {
    if (!last || last.actual_value == null) return null;
    if (last.within_band) {
      return {
        tone: 'text-good-strong dark:text-good',
        text: (
          <>
            Last week it called{' '}
            <span className="font-semibold">{formatValue(last.predicted_value, unit)}</span>,
            you did <span className="font-semibold">{formatValue(last.actual_value, unit)}</span>{' '}
            — landed in band ✓
          </>
        ),
      };
    }
    return {
      tone: 'text-off-strong dark:text-off',
      text: (
        <>
          Last week it called{' '}
          <span className="font-semibold">{formatValue(last.predicted_value, unit)}</span>,
          you did <span className="font-semibold">{formatValue(last.actual_value, unit)}</span>{' '}
          — off by {formatValue(last.abs_error, unit)}
        </>
      ),
    };
  })();

  return (
    <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2">
      {current ? (
        <p className="text-sm text-gray-700 dark:text-gray-200">
          This week's call:{' '}
          <span className="font-semibold text-gray-900 dark:text-white">
            {formatValue(current.predicted_value, unit)}
          </span>{' '}
          <span className="text-gray-600 dark:text-gray-300">
            ({formatRange(current.band_low, current.band_high, unit)})
          </span>
          <span className="text-gray-600 dark:text-gray-300"> · grades next Monday</span>
        </p>
      ) : (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Building enough history to start calling this metric.
        </p>
      )}
      {verdict && <p className={`mt-1 text-sm ${verdict.tone}`}>{verdict.text}</p>}
    </div>
  );
};

const Readout = ({ label, value, unit, tone }) => (
  <div>
    <p className={`text-sm font-semibold uppercase tracking-wider mb-0.5 ${tone}`}>{label}</p>
    <p className="text-base font-bold text-gray-900 dark:text-white whitespace-nowrap">
      {value == null ? '—' : formatValue(value, unit)}
    </p>
  </div>
);

// The full "twin" view of a single metric: trajectory chart, this week's
// prediction, and the Now/Next/12-week/Target readouts. Formerly the heart of
// TwinPage; now rendered inline when a home Dial is expanded. `bare` drops the
// surrounding chrome (title row) for use inside a card that already has one.
const MetricCard = ({ metric, bare = false }) => {
  const t = metric.target || {};
  const verdict = assess(metric);
  const inBand = isInBand(metric);
  const targetText = formatRange(t.ultimate_low, t.ultimate_high, metric.unit);

  const body = (
    <>
      {!bare && (
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-base font-semibold text-gray-900 dark:text-white">{metric.label}</p>
          <div className="flex items-center gap-1.5">
            {metric.prediction && <AccuracyBadge accuracy={metric.prediction.accuracy} />}
            <span className={`text-sm px-2 py-0.5 rounded-full ${toneChip[verdict.tone]}`}>
              {verdict.text}
            </span>
          </div>
        </div>
      )}

      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
        On your current path,{' '}
        <span className="font-semibold text-solar">
          {formatValue(metric.fit_now, metric.unit)}
        </span>{' '}
        today →{' '}
        <span className="font-semibold text-gray-900 dark:text-white">
          {formatValue(metric.projected_value, metric.unit)}
        </span>{' '}
        in {metric.horizon_weeks} weeks.
      </p>

      <TrajectoryChart metric={metric} />

      <PredictionBlock prediction={metric.prediction} unit={metric.unit} />

      <div className="mt-3 flex items-end justify-between gap-3 flex-wrap">
        <Readout
          label="Now"
          value={metric.fit_now}
          unit={metric.unit}
          tone="text-gray-600 dark:text-gray-300"
        />
        {t.next_value != null && !inBand && (
          <Readout
            label={t.next_horizon_weeks ? `Next · ${t.next_horizon_weeks}w` : 'Next'}
            value={t.next_value}
            unit={metric.unit}
            tone="text-good-strong dark:text-good"
          />
        )}
        <Readout
          label="12-wk path"
          value={metric.projected_value}
          unit={metric.unit}
          tone="text-gray-600 dark:text-gray-300"
        />
        <div className="text-right">
          <p className="text-sm font-semibold uppercase tracking-wider text-good-strong dark:text-good mb-0.5">
            Target
          </p>
          <p className="text-base font-bold text-gray-900 dark:text-white whitespace-nowrap">
            {targetText || '—'}
          </p>
        </div>
      </div>

      {inBand && (
        <p className="mt-2 text-sm text-green-700 dark:text-green-400 font-medium">
          Already in your target range — the projection shows whether you hold it.
        </p>
      )}
    </>
  );

  return body;
};

export default MetricCard;
export { toneChip };
